import * as React from 'react';

import { useFunctionInBulk } from '@notino/react-web-utils';

import { useFeatureFlags } from '@context/featureFlags/FeatureFlagsProvider';

import { RecommendationPlacement } from './recommendationPlacement';
import {
  normalizeRecommendationAttributes,
  RecommendationAttributesInput,
  trackRecommendationClick,
  trackRecommendationFailed,
  trackRecommendationInView,
  trackRecommendationShow,
} from './recommendationTracking';

const IN_VIEW_BULK_DELAY_MS = 500;

export type RecommendationProduct = {
  productCode: string;
  productMasterCode: string;
};

type BulkItem = RecommendationProduct & { id: string };

export const useRecommendationTracking = (
  placement: RecommendationPlacement | undefined,
  attributes?: RecommendationAttributesInput
) => {
  const { experiment_pd_bloomreach_tracking_recommendations: enabled } =
    useFeatureFlags();
  const isActive = Boolean(enabled && placement);

  const shownRef = React.useRef(false);
  const failedRef = React.useRef(false);

  const provider = attributes?.provider ?? null;
  const recommendationId = attributes?.recommendationId ?? null;
  const engineName = attributes?.engineName ?? null;

  const normalizedAttributes = React.useMemo(
    () =>
      normalizeRecommendationAttributes({
        provider,
        recommendationId,
        engineName,
      }),
    [provider, recommendationId, engineName]
  );

  const emitInView = React.useCallback(
    (items: BulkItem[]) => {
      if (!placement || items.length === 0) return;
      trackRecommendationInView({
        ...normalizedAttributes,
        placement,
        productCodes: items.map((i) => i.productCode),
        productMasterCodes: items.map((i) => i.productMasterCode),
      });
    },
    [placement, normalizedAttributes]
  );

  const pushInViewItem = useFunctionInBulk<BulkItem, Record<string, never>>(
    emitInView,
    IN_VIEW_BULK_DELAY_MS
  );

  const trackShow = React.useCallback(
    (products: RecommendationProduct[]) => {
      if (!isActive || !placement || shownRef.current || products.length === 0)
        return;
      shownRef.current = true;
      trackRecommendationShow({
        ...normalizedAttributes,
        placement,
        productCodes: products.map((p) => p.productCode),
        productMasterCodes: products.map((p) => p.productMasterCode),
      });
    },
    [isActive, placement, normalizedAttributes]
  );

  const trackInView = React.useCallback(
    (product: RecommendationProduct) => {
      if (!isActive) return;
      pushInViewItem({ ...product, id: product.productCode });
    },
    [isActive, pushInViewItem]
  );

  const trackClick = React.useCallback(
    (product: RecommendationProduct) => {
      if (!isActive || !placement) return;
      trackRecommendationClick({
        ...normalizedAttributes,
        placement,
        ...product,
      });
    },
    [isActive, placement, normalizedAttributes]
  );

  const trackFailed = React.useCallback(
    (message: string) => {
      if (!isActive || !placement || failedRef.current) return;
      failedRef.current = true;
      trackRecommendationFailed({
        ...normalizedAttributes,
        placement,
        message,
      });
    },
    [isActive, placement, normalizedAttributes]
  );

  return { trackShow, trackInView, trackClick, trackFailed };
};
